import axios from 'axios';
import { PAYMENT_METHOD } from './utilityFunctions';
import StripeService from './stripeService';
import Notifications from './notifications';
import { PAYMENT_PROVIDER } from '../commonComponents/propTypes/tenantProp';

const CAPTURE_PAYMENT_STATUS = {
  SUCCEEDED: 'succeeded',
  REQUIRES_CONFIRMATION: 'requires_confirmation',
};

const setupPaymentIntent = async (
  _csrf,
  stripe,
  elements,
  orderSubscriptionId,
  orderId,
  paymentMethod,
  redirectLink,
) => {
  if (!stripe || !elements) {
    // Stripe.js has not yet loaded.
    // Make sure to disable form submission until Stripe.js has loaded.
    return null;
  }

  const redirectURL =
    redirectLink ||
    `${window.location.origin}/shop/order/${orderId}/pay/paypal`;
  const { data: response } = await axios.post(
    '/api/payment/intent/setup',
    {
      orderSubscription: orderSubscriptionId,
      paymentMethod,
    },
    {
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'License',
        'X-CSRF-Token': _csrf,
        _csrf,
      },
    },
  );
  const setupIntent = response.data;
  if (
    paymentMethod === PAYMENT_METHOD.PAYPAL &&
    setupIntent.object !== 'payment_method' && // This will allow us to know that PayPal was already setup
    setupIntent.client_secret
  ) {
    const result = await stripe.confirmPayPalSetup(setupIntent.client_secret, {
      return_url: redirectURL,
    });
    const { error } = result;
    if (error) {
      throw { error };
    }
    return null;
  }
  return setupIntent;
};

/** POST: CREATE MARKETPLACE ORDER */
const createMarketplaceOrder = async (
  _csrf,
  orderData,
  paymentMethodId,
  paymentMethod,
) => {
  let url = '/api/market/order';

  const orderInfo = orderData;
  if (orderInfo.country) {
    url += `?country=${orderInfo.country}`;
  }

  if (orderInfo.currency) {
    url += `&currency=${orderInfo.currency}`;
  }

  // MONDIAL RELAY: Only send "shippingPickupId" if exists
  if (!orderInfo.shippingPickupId) {
    delete orderInfo.shippingPickupId;
  }
  // MONDIAL RELAY: Only send "shippingPickupType" if exists
  if (!orderInfo.shippingPickupType) {
    delete orderInfo.shippingPickupType;
  }
  // MONDIAL RELAY: Only send "shippingPickupAddress" if exists
  if (!orderInfo.shippingPickupAddress) {
    delete orderInfo.shippingPickupAddress;
  }
  // Only send "companyBilling" if exists
  if (!orderInfo.companyBilling) {
    delete orderInfo.companyBilling;
  }

  // Only send "notes" if they exist
  if (!(orderInfo && orderInfo.notes && orderInfo.notes.length > 0)) {
    delete orderInfo.notes;
  }

  // Axios Request to Create Order
  const { data: response } = await axios.post(url, orderInfo, {
    headers: {
      'Accept-Version': '1.0.0',
      'X-Gamification-API': 'Games',
      'X-CSRF-Token': _csrf,
      _csrf,
    },
  });

  return {
    orderData: response.data,
    paymentMethodId,
    paymentMethod,
  };
};

const updateMarketplaceOrder = async (_csrf, orderToUpdate, params) => {
  // BRAVON-6500: the client must never dictate order status — a stale cached
  // value can rewind a paid order back to pending_payment.
  const { status, ...safeParams } = params || {};
  const { data: response } = await axios.put(
    `/api/market/order/${orderToUpdate._id}`,
    safeParams,
    {
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'Games',
        'X-CSRF-Token': _csrf,
        _csrf,
      },
    },
  );

  return response.data;
};

/** POST: CAPTURE PAYMENT MARKETPLACE ORDER */
const capturePaymentMarketplaceOrder = async (
  t,
  _csrf,
  orderData, // order
  paymentMethodId, // card id
  paymentMethod, // "credit_card" "bancontact" etc..
  intentionOfPaymentAlreadyExist, // if order already exist updates payment intent
  paymentId = null,
  paymentProviderType = PAYMENT_PROVIDER.NONE,
  sendEmail = false,
  stripe,
  elements,
  paypalPaymentMethod = null,
  isAfterPaypalSetup = false,
  isOrderPay,
) => {
  let payPalPaymentId = paypalPaymentMethod || null;
  let isAfterPaypalSetupParam = isAfterPaypalSetup || false;
  let runConfirm = true;
  const params = {
    paymentMethod,
  };
  let method = 'post';
  let url = `/api/payment/intent/market/order/${orderData._id}/capture`;

  if (sendEmail) {
    url += '?sendEmail=true';
  }

  if (paymentProviderType === PAYMENT_PROVIDER.STRIPE_INVOICES) {
    url = `/api/order/market/${orderData._id}/capture_payment`;
  }

  if (intentionOfPaymentAlreadyExist) {
    method = 'post';
    url = `/api/payment/intent/market/order/${orderData._id}/payment_method`;
    params.paymentIntent = paymentId;

    if (paymentProviderType === PAYMENT_PROVIDER.STRIPE_INVOICES) {
      method = 'put';
      url = `/api/order/market/${orderData._id}/payment/method`;
    }
  }

  if (
    paymentMethod === PAYMENT_METHOD.CREDIT_CARD ||
    paymentMethod === PAYMENT_METHOD.CREDIT_CARD_SOURCE
  ) {
    params.paymentMethodId = paymentMethodId;
  }

  if (
    orderData.orderSubscription &&
    paymentMethod === PAYMENT_METHOD.PAYPAL &&
    !payPalPaymentId
  ) {
    const paymentMethodPaypalSetup = await setupPaymentIntent(
      _csrf,
      stripe,
      elements,
      orderData.orderSubscription,
      orderData._id,
      paymentMethod,
      null,
    );
    if (!paymentMethodPaypalSetup) {
      return null;
    }
    payPalPaymentId = paymentMethodPaypalSetup.id;
    if (orderData.orderSubscription && payPalPaymentId && isOrderPay) {
      isAfterPaypalSetupParam = true;
      runConfirm = false;
    }
  }
  if (paymentMethod === PAYMENT_METHOD.PAYPAL && payPalPaymentId) {
    params.paymentMethodId = payPalPaymentId;
    params.isAfterPaypalSetup = isAfterPaypalSetupParam;
  } else if (paymentMethod === PAYMENT_METHOD.PAYPAL && !payPalPaymentId) {
    delete params.paymentMethodId;
  }

  const { data: response } = await axios[method](url, params, {
    headers: {
      'Accept-Version': '1.0.0',
      'X-Gamification-API': 'License',
      'X-CSRF-Token': _csrf,
      _csrf,
    },
  });

  const { status, client_secret = null, id = null } = response.data;
  let intentStatus;
  if (!status) {
    intentStatus = CAPTURE_PAYMENT_STATUS.SUCCEEDED;
  } else {
    intentStatus = CAPTURE_PAYMENT_STATUS.REQUIRES_CONFIRMATION;
  }

  return {
    client_secret,
    id,
    status: intentStatus,
    payPalPaymentId,
    runConfirm,
  };
};

/** CLIENT SIDE: STRIPE PAYMENT */
const handleStripePayment = async (
  paymentMethod, // card
  secret, // secret for card
) => StripeService.handlePaymentConfirmation(paymentMethod, secret);

/** POST: CONFIRM PAYMENT MARKETPLACE ORDER */
const confirmMarketplacePayment = async (
  _csrf,
  clientSecret, // secret for payment paymentIntentId
  orderId, // orderId
  paymentProviderType = PAYMENT_PROVIDER.NONE,
) => {
  let url = `/api/payment/intent/market/order/${orderId}/confirm`;
  if (paymentProviderType === PAYMENT_PROVIDER.STRIPE_INVOICES) {
    url = `/api/order/market/${orderId}/confirm_payment`;
  }
  const { data: response } = await axios.post(
    url,
    { paymentIntentId: clientSecret },
    {
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'License',
        'X-CSRF-Token': _csrf,
        _csrf,
      },
    },
  );
  return response;
};

const handleBancontactPayment = async (
  stripe,
  elements,
  onUnload,
  accountHolderName,
  client_secret,
  orderId,
) => {
  if (!stripe || !elements) {
    // Stripe.js has not yet loaded.
    // Make sure to disable form submission until Stripe.js has loaded.
    return null;
  }
  onUnload();
  const result = await stripe.confirmBancontactPayment(client_secret, {
    payment_method: {
      billing_details: {
        name: accountHolderName,
      },
    },
    return_url: `${window.location.origin}/shop/order/${orderId}/pay`,
  });
  const { error } = result;
  if (error) {
    console.log('*ERROR BANCONTACT', error);
    throw { error };
  }
  return result;
};

const handlePaypalPayment = async (
  stripe,
  onUnload,
  accountHolderName,
  client_secret,
  orderId,
  redirectLink,
  payPalPaymentId = null,
  ignorePaypalRedirect = false,
) => {
  if (!stripe) {
    // Stripe.js has not yet loaded.
    // Make sure to disable form submission until Stripe.js has loaded.
    return null;
  }
  onUnload();
  let result;
  const redirectURL =
    redirectLink || `${window.location.origin}/shop/order/${orderId}/pay`;
  try {
    const data = {
      return_url: redirectURL,
    };
    if (payPalPaymentId) {
      data.payment_method = payPalPaymentId;
    }
    result = await stripe.confirmPayPalPayment(client_secret, data);
  } catch (e) {
    console.log('PAYPAL errror', e);
  }
  const { error } = result;

  if (
    payPalPaymentId &&
    !result?.error &&
    result?.paymentIntent?.status === CAPTURE_PAYMENT_STATUS.SUCCEEDED &&
    !ignorePaypalRedirect
  ) {
    window.location.href = `${redirectURL}?redirect_status=${CAPTURE_PAYMENT_STATUS.SUCCEEDED}`;
    return null;
  }
  if (error) {
    throw { error };
  }
  return result;
};

const handleKlarnaPayment = async (
  stripe,
  clientSecret,
  order,
  redirectLink,
) => {
  if (!stripe) {
    // Stripe.js has not yet loaded.
    // Make sure to disable form submission until Stripe.js has loaded.
    return null;
  }

  try {
    const redirectURL =
      redirectLink || `${window.location.origin}/shop/order/${order._id}/pay`;
    const result = await stripe.confirmKlarnaPayment(clientSecret, {
      payment_method: {
        billing_details: {
          address: {
            country: order?.billingAddress?.countryCode?.toUpperCase(),
          },
        },
      },
      return_url: redirectURL,
    });
    return result;
  } catch (e) {
    return e;
  }
};

/** HANDLE DIFFERENT METHODS */
const handleConfirmMarketplacePayment = async (
  _csrf,
  orderId, // order Id,
  status, // intent status
  paymentMethod, // "credit_card" "bancontact" etc.
  paymentProviderType = PAYMENT_PROVIDER.NONE, // Type of payment provider
  paymentIntent, // identifies the payment id for the invoice
  card, // card for client side stripe
  client_secret, // secret for card client side stripe
  stripe, // stripe object for bancontact
  elements, // elements object for bancontact
  accountHolderName, // account name for bancontact
  onUnload, // unloads cart function
  order,
  payPalPaymentId,
  ignorePaypalRedirect = false,
) => {
  if (status === CAPTURE_PAYMENT_STATUS.REQUIRES_CONFIRMATION) {
    if (
      paymentMethod === PAYMENT_METHOD.CREDIT_CARD ||
      paymentMethod === PAYMENT_METHOD.CREDIT_CARD_SOURCE
    ) {
      await handleStripePayment(card, client_secret);
    } else if (paymentMethod === PAYMENT_METHOD.BANCONTACT) {
      await handleBancontactPayment(
        stripe,
        elements,
        onUnload,
        accountHolderName,
        client_secret,
        orderId,
      );
    } else if (paymentMethod === PAYMENT_METHOD.PAYPAL) {
      const result = await handlePaypalPayment(
        stripe,
        onUnload,
        accountHolderName,
        client_secret,
        orderId,
        null,
        payPalPaymentId,
        ignorePaypalRedirect,
      );
      if (!result) {
        return;
      }
    } else if (paymentMethod === PAYMENT_METHOD.KLARNA) {
      await handleKlarnaPayment(stripe, client_secret, order);
    }
  }

  if (
    paymentProviderType !== PAYMENT_PROVIDER.STRIPE_INVOICES ||
    (order &&
      order.total > 0 &&
      paymentProviderType === PAYMENT_PROVIDER.STRIPE_INVOICES)
  ) {
    await confirmMarketplacePayment(
      _csrf,
      paymentIntent,
      orderId,
      paymentProviderType,
    );
  }
};

/** POST: CREDIT CARDS */
const handleAddCreditCard = async (
  _csrf,
  isContextAcademy,
  token,
  attachToUser,
  isPaymentMethod = false,
) => {
  let result;
  if (!isPaymentMethod) {
    let url = '/api/credit-card';
    if (isContextAcademy) {
      url += '?isContextAcademy=true';
    }
    const { data: response } = await axios.post(
      url,
      { token, attachToUser },
      {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'License',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      },
    );
    result = response.data;
  } else {
    const { data: response } = await axios.put(
      '/api/payment/methods/attach',
      {
        paymentMethodId: token,
      },
      {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'License',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      },
    );
    result = {
      ...response.data,
      ...(response?.data?.card || {}),
    };
  }

  return result;
};

/** DELETE: CREDIT CARDS */
const handleRemoveCreditCard = async (_csrf, cardId, isContextAcademy) => {
  let url = '/api/credit-card';
  const params = { cardId };
  if (isContextAcademy) {
    url += '?isContextAcademy=true';
  }

  if (cardId.startsWith('pm_')) {
    url = '/api/payment/methods';
    params.paymentMethodId = cardId;
  }

  await axios.delete(url, {
    data: params,
    headers: {
      'Accept-Version': '1.0.0',
      'X-Gamification-API': 'License',
      'X-CSRF-Token': _csrf,
      _csrf,
    },
  });
};

/** GET: CREDIT CARDS LIST */
const getCreditCardList = async (
  _csrf,
  isContextAcademy = false,
  removeSingleUsageCards,
  isSubscription = false,
) => {
  let url = isSubscription
    ? `/api/payment/methods/list`
    : `/api/credit-card/list?isContextAcademy=${isContextAcademy}`;

  if (!isSubscription && removeSingleUsageCards) {
    url += `&removeSingleUsageCards=${removeSingleUsageCards}`;
  }
  const { data: response } = await axios.get(url, {
    headers: {
      'Accept-Version': '1.0.0',
      'X-Gamification-API': 'License',
      'X-CSRF-Token': _csrf,
      _csrf,
    },
  });

  if (isSubscription) {
    return response.data.map(c => ({ ...c, ...(c.card || {}) })) || [];
  }
  return response.data;
};

/** HANDLE REMOVE THE CREDIT CARDS */
const cleanSingUseCards = async (_csrf, loggedUser) => {
  try {
    if (loggedUser) {
      const updatedCreditCards = await getCreditCardList(_csrf, false);

      updatedCreditCards.map(c => {
        if (c && c.usage === 'single_use') {
          handleRemoveCreditCard(_csrf, c.id, false);
        }
        return c;
      });
    }
  } catch (err) {
    Notifications.showErrorFromRequest(err);
  }
};

const handleRemoveSingleUseCards = async (_csrf, loggedUser) => {
  window.addEventListener('beforeunload', async () => {
    await cleanSingUseCards(_csrf, loggedUser);
  });
};

export {
  createMarketplaceOrder,
  updateMarketplaceOrder,
  capturePaymentMarketplaceOrder,
  handleConfirmMarketplacePayment,
  handleAddCreditCard,
  handleRemoveCreditCard,
  getCreditCardList,
  handleRemoveSingleUseCards,
  cleanSingUseCards,
  handlePaypalPayment,
  handleKlarnaPayment,
};
